import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStore, faExclamationTriangle, faSpinner, faPlus } from '@fortawesome/free-solid-svg-icons';
import { Chain } from '../../coffee_clusters/hooks/useChains';
import { cn } from '../../../ui/utils/cn';

interface ChainListProps {
    chains: Chain[];
    loading: boolean;
    error?: string | null;
    onRetry?: () => void;
    selectedId: string | null;
    onSelect: (chain: Chain) => void;
    onCreateClick: () => void;
}

export function ChainList({
    chains,
    loading,
    error,
    onRetry,
    selectedId,
    onSelect,
    onCreateClick
}: ChainListProps) {
    if (loading) {
        return (
            <div className="flex items-center justify-center py-8">
                <FontAwesomeIcon icon={faSpinner} className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="text-center py-8 text-sm space-y-2">
                <p className="text-destructive flex items-center justify-center gap-2">
                    <FontAwesomeIcon icon={faExclamationTriangle} />
                    {error}
                </p>
                {onRetry && (
                    <button type="button" onClick={onRetry} className="text-primary hover:underline">
                        Retry
                    </button>
                )}
            </div>
        );
    }

    return (
        <div className="space-y-2">
            <button
                type="button"
                onClick={onCreateClick}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-muted-foreground/40 hover:border-primary/50 hover:bg-muted/30 transition-colors text-sm"
            >
                <FontAwesomeIcon icon={faPlus} className="h-4 w-4" />
                Create chain
            </button>
            {chains.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground text-sm">
                    No chains yet. Create one to get started.
                </div>
            ) : (
                chains.map((chain) => {
                    const isSelected = selectedId === chain._id;
                    return (
                        <div
                            key={chain._id}
                            role="button"
                            tabIndex={0}
                            onClick={() => onSelect(chain)}
                            onKeyDown={(e) => e.key === 'Enter' && onSelect(chain)}
                            className={cn(
                                'flex items-center justify-between gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors',
                                isSelected
                                    ? 'border-primary bg-primary/10'
                                    : 'border-transparent hover:bg-muted/30'
                            )}
                        >
                            <div className="flex items-center gap-2 min-w-0">
                                <FontAwesomeIcon icon={faStore} className="h-4 w-4 text-muted-foreground shrink-0" />
                                <span className="font-medium truncate">{chain.name}</span>
                            </div>
                            <span className="text-xs text-muted-foreground shrink-0">
                                {chain.totalLocations ?? 0}
                            </span>
                        </div>
                    );
                })
            )}
        </div>
    );
}
